import React from "react";
import { StyleSheet, View, Text, Platform } from "react-native";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import LHSectionHeader from "../../components/LHSectionHeader";
import LHMiscellaneousObservations from "../../components/LHMiscellaneousObservations.fixed.new";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { DropdownItem } from "../../types/observation";
import CrossPlatformDropdown from "../../components/CrossPlatformDropdown";
import FontFamily from "../../constants/fonts";
import { useTheme } from "../../theme";

interface Props {
  selectedEmployee: DropdownItem | null;
  setSelectedEmployee: (value: DropdownItem | null) => void;
  employeeOptions: DropdownItem[];
  error?: boolean;
  errorMessage?: string; // Added errorMessage prop
  isDisabled?: boolean;
  onSignatureCapture?: (signature: string) => void;
  testID?: string; // Added testID prop
}

const SparcWalkInspectionMiscellaneous: React.FC<Props> = ({
  selectedEmployee,
  setSelectedEmployee,
  employeeOptions,
  error = false,
  errorMessage = "This field is mandatory",
  isDisabled = false,
  onSignatureCapture,
  testID, // Destructure testID prop
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const selectedWalk = useSelector(
    (state: RootState) => state.sparcWalk.selectedWalk
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const selectedWalkData = selectedWalk?.walkData;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleSignatureCapture = (dataUrl: string): void => {
    if (onSignatureCapture) {
      onSignatureCapture(dataUrl);
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.container}>
        <View style={styles.dropdownWrapper}>
          <View style={styles.labelRow}>
            <View style={styles.labelLeft}>
              <Text style={styles.fieldLabel}>Employee Conversed</Text>
            </View>
            <Text
              style={[
                styles.requirementText,
                isDark && { color: colors.text.secondary },
              ]}
            >
              *Required
            </Text>
          </View>

          <CrossPlatformDropdown
            label=""
            placeholder="Select an Employee"
            childList={employeeOptions}
            selectedValue={selectedEmployee}
            setSelectedValue={(value) => {
              // Handle type conversion - ensure we get a single value (not an array)
              const singleValue = Array.isArray(value) ? value[0] : value;
              setSelectedEmployee(singleValue as DropdownItem | null);
            }}
            selection="single"
            isClearable
            isRequired
            isDisabled={isDisabled}
            style={styles.responsiveDropdown}
            testID={testID} // Pass testID to CrossPlatformDropdown
          />

          {error && <Text style={[styles.errorText, isDark && { color: colors.status.error }]}>{errorMessage}</Text>}
        </View>
      </View>
      <View style={styles.miscellaneousObservationsWrapper}>
        {/* Card with integrated header and body (matches other SPARC components) */}
        <View style={styles.cardContainer}>
          <LHSectionHeader
            title="Miscellaneous Observations"
            containerStyle={
              isDark
                ? { ...styles.sectionHeader, backgroundColor: colors.brand.primary }
                : styles.sectionHeader
            }
            textStyle={styles.header1text}
          />

          <View
            style={[
              styles.miscellaneousObservationsContainer,
              isDark && {
                backgroundColor: colors.background.surface,
                borderColor: colors.border.default,
              },
            ]}
          >
            <LHMiscellaneousObservations
              disabled={isDisabled}
              stateKey="sparcWalk"
              containerName="sparc"
            />
          </View>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    position: "relative",
    zIndex: 1,
  },
  headerText: {
    fontSize: 16,
    fontFamily: FontFamily.regular,
  },
  dropdownWrapper: {
    width: Platform.OS === "web" ? "30%" : "100%",
    position: "relative",
    zIndex: 2,
    marginTop: 24,
  },
  errorText: {
    color: "#dc3545",
    fontSize: 12,
    marginTop: 4,
    fontFamily: FontFamily.regular,
  },
  responsiveDropdown: {
    position: "relative",
  },
  labelRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 6,
  },
  labelLeft: {
    flexDirection: "row",
    alignItems: "center",
  },
  fieldLabel: {
    fontSize: 14,
    fontWeight: "600",
    color: "#FFFFFF",
    marginRight: 6,
    fontFamily: FontFamily.regular,
  },
  infoIcon: {
    marginTop: 2,
  },
  requirementText: {
    color: "#FFFFFF",
    textAlign: "right",
    fontSize: 10,
    fontWeight: "500",
    lineHeight: 14,
    fontFamily: FontFamily.regular,
  },
  cardContainer: {
    borderRadius: 6,
    // allow header to expand beyond original bounds when wrapping
    overflow: Platform.OS === "ios" ? "visible" : "hidden",
  },
  sectionHeader: {
    backgroundColor: "#3582E6",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    // use minHeight so header can expand to 2 lines when needed
    minHeight: 48,
  },
  header1text: {
    fontSize: 14,
    fontWeight: "700",
    lineHeight: 20,
    fontFamily: FontFamily.bold,
  },
  miscellaneousObservationsContainer: {
    borderColor: "#e9ecef",
    borderWidth: 1,
    borderTopWidth: 0,
    borderRadius: 6,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    backgroundColor: "white",
    padding: 16,
  },
  miscellaneousObservationsWrapper: {
    marginTop: 24,
  },
});

export default SparcWalkInspectionMiscellaneous;
